import React, { Dispatch, SetStateAction, useState } from 'react';
import {
    formatDate,
    formatNumber,
    getTransactionStatus
} from '../../../utils/helper';
import { Dropdown, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';

import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    SyncOutlined
} from '@ant-design/icons';
import { Transactions } from '../../../interfaces/Transactions';
import DashboardTableMobileCard from '../../../components/DashboardTableMobileCard';
import Button from '../../../components/button/Button';
import FilterTransactions from './FilterTransactions';
import { Beneficiary } from '../../../interfaces/Beneficiary';
import CustomTable from '../../../components/CustomTable';
import { ExportTransactions } from './ExportTransactions';
import {
    ExportTwoIcon,
    RefreshCircleIcon,
    MoreIcon
} from '../../../components/icons';
import { FilterParams, PagingParams } from '../../../interfaces/Filter';

interface TransactionsTableProps {
    isTransactionsLoading: boolean;
    transactionsList: Transactions[];
    fetchTransactions: () => void;
    isFetchingTransactions: boolean;
    beneficiaries: Beneficiary[];
    transactionFilterOptions: FilterParams;
    setTransactionFilterOptions: Dispatch<SetStateAction<FilterParams>>;
    transactionPaging: PagingParams;
}

const TransactionsTable = ({
    isTransactionsLoading,
    isFetchingTransactions,
    transactionsList,
    transactionPaging,
    transactionFilterOptions,
    setTransactionFilterOptions,
    fetchTransactions,
    beneficiaries
}: TransactionsTableProps) => {
    const navigate = useNavigate();

    const [openExport, setOpenExport] = useState(false);

    return (
        <div className='bg-white rounded-[8px] overflow-hidden'>
            <div className='flex justify-between items-center px-[24px] py-[20px]'>
                <h4 className='text-ebony font-semibold text-lg'>
                    Transactions
                </h4>

                <div className='hidden lg:flex gap-2'>
                    <Button
                        leftIcon={<ExportTwoIcon />}
                        text='Export'
                        variant='secondaryOutline'
                        onClick={() => setOpenExport(true)}
                        size='sm'
                    />
                    <FilterTransactions
                        {...{ setTransactionFilterOptions, beneficiaries }}
                    />
                    <Button
                        leftIcon={<RefreshCircleIcon />}
                        text='Refresh'
                        variant='secondaryOutline'
                        onClick={fetchTransactions}
                        size='sm'
                    />
                </div>

                <div className='lg:hidden  relative'>
                    <Dropdown
                        menu={{
                            items: [
                                {
                                    key: '1',
                                    label: (
                                        <Button
                                            leftIcon={<RefreshCircleIcon />}
                                            text='Refresh'
                                            variant='tertiary'
                                            onClick={fetchTransactions}
                                        />
                                    )
                                },
                                {
                                    key: '2',
                                    label: (
                                        <FilterTransactions
                                            {...{
                                                setTransactionFilterOptions,
                                                beneficiaries
                                            }}
                                        />
                                    )
                                },
                                {
                                    key: '3',
                                    label: (
                                        <Button
                                            leftIcon={<ExportTwoIcon />}
                                            text='Export'
                                            variant='tertiary'
                                            onClick={() => setOpenExport(true)}
                                        />
                                    )
                                }
                            ]
                        }}
                        trigger={['click']}
                        dropdownRender={(menu) => (
                            <div className='w-[40vw]'>
                                {React.cloneElement(menu as React.ReactElement)}
                            </div>
                        )}
                    >
                        <button>
                            <MoreIcon />
                        </button>
                    </Dropdown>
                </div>
            </div>
            <ExportTransactions
                open={openExport}
                onClose={() => setOpenExport(false)}
                params={transactionFilterOptions}
            />
            <CustomTable
                data={transactionsList}
                loadingData={isTransactionsLoading || isFetchingTransactions}
                mobileComponent={<DashboardTableMobileCard />}
                onMobileRowClick={(data) =>
                    navigate(
                        `/payments/transactions/${data.transactionReference}`
                    )
                }
                columns={[
                    {
                        title: 'Status',
                        dataIndex: 'status',
                        key: 'status',
                        render: (_, { status }) => (
                            <Tag
                                bordered={false}
                                color={
                                    getTransactionStatus(status) ===
                                    'Processing'
                                        ? 'processing'
                                        : getTransactionStatus(status) ===
                                          'Completed'
                                        ? 'success'
                                        : getTransactionStatus(status) ===
                                          'Initiated'
                                        ? 'warning'
                                        : 'error'
                                }
                                icon={
                                    getTransactionStatus(status) ===
                                    'Processing' ? (
                                        <SyncOutlined spin />
                                    ) : getTransactionStatus(status) ===
                                      'Completed' ? (
                                        <CheckCircleOutlined />
                                    ) : getTransactionStatus(status) ===
                                      'Initiated' ? (
                                        <ExclamationCircleOutlined />
                                    ) : (
                                        <CloseCircleOutlined />
                                    )
                                }
                            >
                                {getTransactionStatus(status)}
                            </Tag>
                        )
                    },
                    {
                        title: 'Date',
                        dataIndex: 'transactionStartDate',
                        key: 'transactionStartDate',
                        render: (_, { transactionStartDate }) => (
                            <span>{formatDate(transactionStartDate)}</span>
                        )
                    },
                    {
                        title: 'Corporate',
                        dataIndex: 'businessName',
                        key: 'businessName'
                    },
                    {
                        title: 'Transaction ID',
                        dataIndex: 'transactionReference',
                        key: 'transactionReference',
                        render: (text, { transactionReference }) => (
                            <span
                                className='cursor-pointer text-appcolorprimary'
                                onClick={() => {
                                    navigate(
                                        `/payments/transactions/${transactionReference}`
                                    );
                                }}
                            >
                                {text}
                            </span>
                        )
                    },
                    {
                        title: 'From',
                        dataIndex: 'amountFrom',
                        key: 'amountFrom',
                        render: (text, { fromCurrencyCode }) => (
                            <span>
                                {fromCurrencyCode}{' '}
                                {formatNumber(parseFloat(text))}
                            </span>
                        )
                    },
                    {
                        title: 'To',
                        dataIndex: 'amountTo',
                        key: 'amountTo',
                        render: (text, { toCurrencyCode }) => (
                            <span>
                                {toCurrencyCode}{' '}
                                {formatNumber(parseFloat(text))}
                            </span>
                        )
                    },
                    {
                        title: 'Rate',
                        dataIndex: 'rateValue',
                        key: 'rateValue',
                        render: (text) => (
                            <span>{parseFloat(text).toFixed(5)}</span>
                        )
                    },
                    {
                        title: 'Type',
                        dataIndex: 'transactionAction',
                        key: 'transactionAction'
                    }
                ]}
                showPagination
                paging={{
                    ...transactionFilterOptions,
                    total: transactionPaging.total
                }}
                updatePage={(value) => {
                    setTransactionFilterOptions({
                        ...transactionFilterOptions,
                        page: value
                    });
                }}
            />
        </div>
    );
};

export default TransactionsTable;
